import { payrollService } from '../../payroll/services/payrollService';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, CardHeader, DatePicker, Icon, Input, MoneyInput, PageHeader, Select, Textarea, useToast } from '../../../components/ui';
import { CustomerPicker, InvoicePicker, PurchasePicker, StaffPicker, VendorPicker } from '../../../components/common/Pickers/Pickers';
import { FormSection, KeyValues, TwoColumn } from '../../../components/common/Parts/Parts';
import { cn } from '../../../utils/cn';
import { labelOf } from '../../../utils/labels';
import { PAYMENT_MODE_OPTIONS, PAYMENT_MODES } from '../../../constants/payments';
import { serverFieldErrors, trimOrUndef } from '../../../utils/validation';
import { useForm } from '../../../hooks/useForm';
import { money } from '../../../utils/currency';
import { toISODate } from '../../../utils/date';
import { customerService } from '../../customers/services/customerService';
import { invoiceService } from '../../invoices/services/invoiceService';
import { purchaseService } from '../../purchases/services/purchaseService';
import { transactionService } from '../services/transactionService';
import { teamService } from '../../team/services/teamService';
import type { TxnCategory, TxnDirection, TxnRefType } from '../types/transactionTypes';
import type { FormEvent } from 'react';
import type { FormErrors } from '../../../hooks/useForm';
import type { PaymentMode, PersonRef } from '../../../types/common';

// The kinds of payment the office records, with what each one needs.
interface TxnKind {
  value: string;
  label: string;
  icon: string;
  /** Fixed direction, or null to let the user choose */
  dir: 'in' | 'out' | null;
  category?: string;
  party?: 'customer' | 'vendor';
}

/** An invoice, supplier bill or salary slip being paid. */
interface PayableDoc {
  _id: string;
  balanceAmount: number;
  grandTotal: number;
  invoiceNo?: string;
  purchaseNo?: string;
  slipNo?: string;
  customer?: { name?: string };
  vendor?: { name?: string };
  [key: string]: any;
}

const KINDS: TxnKind[] = [
  { value: 'invoice_payment', label: 'Customer paid an invoice', icon: 'receipt', dir: 'in' },
  { value: 'advance_customer', label: 'Advance from customer', icon: 'moneyIn', dir: 'in', category: 'advance', party: 'customer' },
  { value: 'purchase_payment', label: 'Paid a supplier bill', icon: 'cart', dir: 'out' },
  { value: 'advance_vendor', label: 'Advance to supplier', icon: 'moneyOut', dir: 'out', category: 'advance', party: 'vendor' },
  { value: 'refund_customer', label: 'Refund to customer', icon: 'refresh', dir: 'out', category: 'refund', party: 'customer' },
  { value: 'refund_vendor', label: 'Refund from supplier', icon: 'refresh', dir: 'in', category: 'refund', party: 'vendor' },
  { value: 'salary', label: 'Paid salary', icon: 'users', dir: 'out' },
  { value: 'staff_advance', label: 'Advance to staff', icon: 'moneyOut', dir: 'out' },
  { value: 'capital', label: 'Owner added money', icon: 'wallet', dir: 'in' },
  { value: 'withdrawal', label: 'Owner took money', icon: 'wallet', dir: 'out' },
  { value: 'other', label: 'Other', icon: 'swap', dir: null },
];
const DIRECTIONS = [
  { value: 'credit', label: 'Money in' },
  { value: 'debit', label: 'Money out' },
];

function initialKind(params: URLSearchParams) {
  const c = params.get('category');
  if (c === 'purchase_payment' || params.get('purchaseId') || params.get('vendorId')) return 'purchase_payment';
  if (params.get('payslipId')) return 'salary';
  if (c && KINDS.some((k) => k.value === c)) return c;
  return 'invoice_payment';
}

export default function TransactionForm() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [ref, setRef] = useState<PayableDoc | null>(null); // selected invoice or purchase
  const [party, setParty] = useState<PersonRef | null>(null);
  const { form, setForm, set, errors, setErrors } = useForm({
    kind: initialKind(params), refId: params.get('invoiceId') || params.get('purchaseId') || null, partyId: params.get('customerId') || params.get('staffId') || null,
    amount: null, mode: 'upi' as PaymentMode, referenceNo: '', date: toISODate(new Date()), note: '', direction: 'credit', partyName: '',
  });
  const vendorFilter = params.get('vendorId');
  const kind = KINDS.find((k) => k.value === form.kind) ?? KINDS[KINDS.length - 1];

  // Coming from an invoice / purchase: load it and fill the balance.
  useEffect(() => {
    const invoiceId = params.get('invoiceId');
    const purchaseId = params.get('purchaseId');
    const ref: Promise<PayableDoc> | null = invoiceId ? invoiceService.get(invoiceId) : purchaseId ? purchaseService.get(purchaseId) : null;
    ref?.then((doc) => pickRef(doc._id, doc)).catch(() => {});
    const cid = params.get('customerId');
    if (cid) customerService.get(cid).then(setParty).catch(() => {});
    const staffId = params.get('staffId');
    if (staffId) teamService.get(staffId).then(setParty).catch(() => {});
    // Paying a salary slip: the person and amount come from the slip.
    const payslipId = params.get('payslipId');
    if (payslipId) {
      payrollService
        .get(payslipId)
        .then((slip) => {
          setRef({ ...slip, grandTotal: slip.netAmount });
          setParty({ _id: slip.userId, name: slip.staff?.name });
          setForm((f) => ({ ...f, refId: slip._id, partyId: slip.userId, amount: slip.balanceAmount, note: f.note || `Salary ${slip.slipNo}` }));
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function pickRef(id: string | null, doc: PayableDoc | null) {
    setRef(doc);
    setForm((f) => ({ ...f, refId: id, amount: doc ? doc.balanceAmount : f.amount }));
    setErrors((e) => ({ ...e, refId: undefined }));
  }

  const chooseKind = (value) => {
    setForm((f) => ({ ...f, kind: value, refId: null, partyId: value.endsWith('customer') ? f.partyId : null }));
    setRef(null);
    if (!value.endsWith('customer')) setParty(null);
    setErrors({});
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: FormErrors = {};
    if (!(form.amount > 0)) errs.amount = 'Enter the amount';
    if (['invoice_payment', 'purchase_payment'].includes(form.kind) && !form.refId) errs.refId = form.kind === 'invoice_payment' ? 'Pick the invoice' : 'Pick the supplier bill';
    if ((kind.party || form.kind === 'salary' || form.kind === 'staff_advance') && !form.partyId) errs.partyId = 'Pick who this is for';
    if (ref && form.amount > ref.balanceAmount) errs.amount = `More than the balance (${money(ref.balanceAmount)})`;
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const body = {
      amount: form.amount as number,
      mode: form.mode,
      referenceNo: trimOrUndef(form.referenceNo),
      date: form.date || undefined,
      note: trimOrUndef(form.note),
      category: (kind.category ?? form.kind) as TxnCategory,
      ...(form.kind === 'invoice_payment' || form.kind === 'purchase_payment' ? { refType: (form.kind === 'invoice_payment' ? 'invoice' : 'purchase') as TxnRefType, refId: form.refId ?? undefined } : {}),
      ...(kind.party ? { partyType: kind.party, partyId: form.partyId ?? undefined } : {}),
      ...(form.kind === 'salary' || form.kind === 'staff_advance' ? { partyType: 'staff' as const, partyId: form.partyId ?? undefined } : {}),
      ...(form.kind === 'salary' && ref?.slipNo ? { refType: 'payslip' as const, refId: ref._id } : {}),
      ...(['capital', 'withdrawal', 'other'].includes(form.kind) && form.partyName.trim() ? { partyName: form.partyName.trim() } : {}),
      ...(form.kind === 'other' ? { direction: form.direction as TxnDirection } : {}),
    };
    setSaving(true);
    try {
      const data = await transactionService.create(body);
      toast.success(`Payment ${data.transactionNo} saved`);
      const back = params.get('invoiceId')
        ? `/invoices/${params.get('invoiceId')}`
        : params.get('purchaseId')
          ? `/purchases/${params.get('purchaseId')}`
          : params.get('payslipId')
            ? `/payroll/${params.get('payslipId')}`
            : params.get('from') === 'advances'
              ? '/staff-advances'
              : `/transactions/${data._id}`;
      navigate(back, { replace: true });
    } catch (err) {
      setErrors(serverFieldErrors(err));
      toast.error(err.message);
      setSaving(false);
    }
  };

  const dir = form.kind === 'other' ? (form.direction === 'credit' ? 'in' : 'out') : kind.dir;
  const actions = (
    <>
      <Button variant="outline" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
      <Button type="submit" form="txn-form" leftIcon="check" loading={saving}>Save payment</Button>
    </>
  );

  return (
    <>
      <PageHeader title="Record payment" subtitle="Money received or paid. Mistakes are fixed later by reversing the entry." actions={actions} />
      <form id="txn-form" onSubmit={submit} noValidate>
        <TwoColumn
          main={
            <>
              <Card>
                <CardHeader title="What is this payment?" className="pb-4" />
                <div role="radiogroup" aria-label="Payment type" className="grid gap-2 border-t border-line p-5 sm:grid-cols-2 xl:grid-cols-3">
                  {KINDS.map((k) => (
                    <button
                      key={k.value}
                      type="button"
                      role="radio"
                      aria-checked={form.kind === k.value}
                      onClick={() => chooseKind(k.value)}
                      className={cn(
                        'flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors',
                        form.kind === k.value ? 'border-brand-600 bg-brand-50 font-medium text-brand-700 ring-1 ring-brand-600' : 'border-line hover:border-navy-300',
                      )}
                    >
                      <Icon name={k.icon} size={17} className={form.kind === k.value ? 'text-brand-600' : 'text-ink-400'} />
                      <span className="flex-1">{k.label}</span>
                      {k.dir && <span className={cn('text-xs', k.dir === 'in' ? 'text-emerald-600' : 'text-ink-400')}>{k.dir === 'in' ? 'In' : 'Out'}</span>}
                    </button>
                  ))}
                </div>
              </Card>

              <FormSection title="Details">
                {form.kind === 'invoice_payment' && (
                  <InvoicePicker required query={params.get('customerId') ? { customerId: params.get('customerId') } : undefined} value={form.refId} selectedLabel={ref ? `${ref.invoiceNo} · ${ref.customer?.name}` : ''} onChange={pickRef} error={errors.refId} containerClassName="sm:col-span-2" />
                )}
                {form.kind === 'purchase_payment' && (
                  <PurchasePicker required query={vendorFilter ? { vendorId: vendorFilter } : undefined} value={form.refId} selectedLabel={ref ? `${ref.purchaseNo} · ${ref.vendor?.name}` : ''} onChange={pickRef} error={errors.refId} containerClassName="sm:col-span-2" />
                )}
                {kind.party === 'customer' && (
                  <CustomerPicker required value={form.partyId} selectedLabel={party?.name} onChange={(id, c) => { set('partyId')(id); setParty(c); }} error={errors.partyId} containerClassName="sm:col-span-2" />
                )}
                {kind.party === 'vendor' && (
                  <VendorPicker required value={form.partyId} selectedLabel={party?.name} onChange={(id, v) => { set('partyId')(id); setParty(v); }} error={errors.partyId} containerClassName="sm:col-span-2" />
                )}
                {form.kind === 'salary' && ref?.slipNo && <Input label="Salary slip" value={`${ref.slipNo} · ${ref.staff?.name ?? ''}`} disabled containerClassName="sm:col-span-2" />}
                {(form.kind === 'staff_advance' || (form.kind === 'salary' && !ref?.slipNo)) && (
                  <StaffPicker required value={form.partyId} selectedLabel={party?.name} onChange={(id, u) => { set('partyId')(id); setParty(u); }} error={errors.partyId} containerClassName="sm:col-span-2" hint={form.kind === 'salary' ? 'For wages worked out from attendance, make a salary slip in Salary & wages instead' : 'Recovered later from the salary slip'} />
                )}
                {form.kind === 'other' && <Select label="Direction" options={DIRECTIONS} value={form.direction} onChange={set('direction')} searchable={false} />}
                {['capital', 'withdrawal', 'other'].includes(form.kind) && <Input label="Paid by / to" value={form.partyName} onChange={set('partyName')} placeholder="Optional" />}

                <MoneyInput label="Amount" required value={form.amount} onChange={set('amount')} error={errors.amount} placeholder="0" hint={ref ? `Balance ${money(ref.balanceAmount)}` : undefined} />
                <DatePicker label="Date" value={form.date} onChange={set('date')} clearable={false} max={toISODate(new Date())} />
                <Select label="Mode" options={PAYMENT_MODE_OPTIONS} value={form.mode} onChange={set('mode')} searchable={false} />
                <Input label="UTR / cheque no" value={form.referenceNo} onChange={set('referenceNo')} placeholder="Optional" maxLength={60} />
                <Textarea label="Note" rows={2} value={form.note} onChange={set('note')} maxLength={500} containerClassName="sm:col-span-2" />
              </FormSection>
              <div className="flex justify-end gap-2">{actions}</div>
            </>
          }
          aside={
            <Card>
              <CardHeader title="Summary" />
              <div className="p-5 pt-4">
                <KeyValues
                  rows={[
                    ['Type', kind.label],
                    ['Direction', dir === 'in' ? 'Money in' : 'Money out', { tone: dir === 'in' ? 'text-emerald-700' : undefined }],
                    ['Mode', labelOf(PAYMENT_MODES, form.mode)],
                    ref && [ref.slipNo ? 'Net pay' : 'Bill total', money(ref.grandTotal)],
                    ref && ['Balance now', money(ref.balanceAmount)],
                    ref && ['Balance after', money(Math.max(0, ref.balanceAmount - (form.amount ?? 0))), { strong: true }],
                    ['Amount', money(form.amount), { strong: true }],
                  ]}
                />
              </div>
            </Card>
          }
        />
      </form>
    </>
  );
}
